import { useState } from 'react';
import { Link } from 'react-router-dom';
import './MarketplaceSection.css';
import underlineImg from '../assets/underline.png';
import { IconLightning } from './Icons';

const TAGS = ['All', 'Marketplace', 'E-Commerce', 'Fashion', 'Beauty', 'Social Commerce', 'Quick Commerce', 'International', 'Advertising'];
const LOGO = (domain) => `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

const platforms = [
  {
    name: 'Amazon',
    tag: 'Marketplace',
    category: 'PPC & Marketplace',
    logo: LOGO('amazon.com'),
    route: '/services/amazon',
    services: ['Listing Optimization', 'PPC Management', 'A+ Content'],
  },
  {
    name: 'Shopify',
    tag: 'E-Commerce',
    category: 'E-Commerce & CRO',
    logo: LOGO('shopify.com'),
    route: '/services/shopify',
    services: ['Store SEO', 'CRO', 'Speed Optimisation'],
  },
  {
    name: 'Walmart',
    tag: 'Marketplace',
    category: 'Marketplace & Listings',
    logo: LOGO('walmart.com'),
    route: '/services/walmart',
    services: ['Listing SEO', 'WFS Strategy', 'Buy Box'],
  },
  {
    name: 'Noon',
    tag: 'Marketplace',
    category: 'Middle East Marketplace',
    logo: LOGO('noon.com'),
    route: '/services/noon',
    services: ['Bilingual Listings', 'Noon Express', 'Category SEO'],
  },
  {
    name: 'eBay',
    tag: 'Marketplace',
    category: 'Global Marketplace',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1b/EBay_logo.svg/1280px-EBay_logo.svg.png',
    route: '/services/ebay',
    services: ['Cassini SEO', 'Title Optimisation', 'Promoted Listings'],
  },
  {
    name: 'AliExpress',
    tag: 'International',
    category: 'International Sales',
    logo: LOGO('aliexpress.com'),
    route: '/services/aliexpress',
    services: ['Product SEO', 'Visual Search', 'Store Setup'],
  },
  {
    name: 'Flipkart',
    tag: 'Marketplace',
    category: 'Indian Marketplace',
    logo: LOGO('flipkart.com'),
    route: '/services/flipkart',
    services: ['Listing SEO', 'Flipkart Ads', 'F-Assured'],
  },
  {
    name: 'Meesho',
    tag: 'Social Commerce',
    category: 'Social Commerce',
    logo: LOGO('meesho.com'),
    route: '/services/meesho',
    services: ['Catalogue SEO', 'Pricing Strategy', 'Scorecard'],
  },
  {
    name: 'Myntra',
    tag: 'Fashion',
    category: 'Fashion Marketplace',
    logo: LOGO('myntra.com'),
    route: '/services/myntra',
    services: ['Fashion SEO', 'Trend Research', 'Brand Store'],
  },
  {
    name: 'Nykaa',
    tag: 'Beauty',
    category: 'Beauty & Wellness',
    logo: LOGO('nykaa.com'),
    route: '/services/nykaa',
    services: ['Beauty SEO', 'Ingredient Keywords', 'A+ Content'],
  },
  {
    name: 'AJIO',
    tag: 'Fashion',
    category: 'Fashion & Lifestyle',
    logo: LOGO('ajio.com'),
    route: '/services/ajio',
    services: ['Brand Onboarding', 'Catalogue SEO', 'LUXE Positioning'],
  },
  {
    name: 'Blinkit',
    tag: 'Quick Commerce',
    category: 'Quick Commerce',
    logo: LOGO('blinkit.com'),
    route: '/services/blinkit',
    services: ['Listing SEO', 'Dark Store Strategy', 'SOV Management'],
  },
  {
    name: 'Google Ads',
    tag: 'Advertising',
    category: 'Search & Shopping',
    logo: LOGO('ads.google.com'),
    route: '/services/performance-marketing',
    services: ['Search Ads', 'Shopping Ads', 'Performance Max'],
  },
  {
    name: 'Meta Ads',
    tag: 'Advertising',
    category: 'Social Advertising',
    logo: LOGO('meta.com'),
    route: '/services/performance-marketing',
    services: ['Facebook Ads', 'Remarketing', 'Catalog Sales'],
  },
  {
    name: 'Instagram Ads',
    tag: 'Advertising',
    category: 'Visual Commerce',
    logo: LOGO('instagram.com'),
    route: '/services/performance-marketing',
    services: ['Story Ads', 'Reels Ads', 'Shopping'],
  },
  {
    name: 'YouTube Ads',
    tag: 'Advertising',
    category: 'Video Advertising',
    logo: LOGO('youtube.com'),
    route: '/services/performance-marketing',
    services: ['In-Stream Ads', 'Bumper Ads', 'Discovery'],
  },
];

export default function MarketplaceSection() {
  const [activeTag, setActiveTag] = useState('All');

  const filtered = activeTag === 'All' ? platforms : platforms.filter(p => p.tag === activeTag);

  return (
    <section className="mp-section" aria-label="Platforms We Dominate">

      <div className="mp-heading">
        <div className="badge mp-badge">
          <span className="badge-icon"><IconLightning /></span>
          OUR SERVICES
        </div>
        <h2 className="mp-title">
          Platforms &amp; Channels we
          <span className="mp-title-gradient"> dominate</span>
        </h2>
        <img src={underlineImg} alt="" className="mp-underline" aria-hidden="true" />
        <p className="mp-subtitle">Marketplaces, advertising &amp; more — click any to explore our full services, process, and deliverables.</p>
      </div>

      <div className="mp-filters" role="tablist" aria-label="Filter by category">
        {TAGS.map(tag => (
          <button
            key={tag}
            role="tab"
            aria-selected={activeTag === tag}
            className={`mp-filter-btn${activeTag === tag ? ' mp-filter-btn--active' : ''}`}
            onClick={() => setActiveTag(tag)}
          >
            {tag}
          </button>
        ))}
      </div>

      <div className="mp-grid" role="list">
        {filtered.map((p) => (
          <Link
            key={p.name}
            to={p.route}
            role="listitem"
            className={`mp-card${p.tag === 'Advertising' ? ' mp-card--ad' : ''}`}
            aria-label={`View ${p.name} service details`}
          >
            <div className="mp-icon-box">
              <img src={p.logo} alt={p.name + ' logo'} className="mp-logo-img" />
            </div>
            <div className="mp-card-info">
              <div className="mp-name">{p.name}</div>
              <div className="mp-category">{p.category}</div>
            </div>
            <div className="mp-card-arrow">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </div>
          </Link>
        ))}
      </div>

      <div className="mp-cta">
        <p className="mp-cta-text">Marketplace growth + paid advertising — every channel, one team.</p>
        <div className="mp-cta-links">
          <Link to="/services/amazon" className="mp-cta-pill">Amazon →</Link>
          <Link to="/services/flipkart" className="mp-cta-pill">Flipkart →</Link>
          <Link to="/services/performance-marketing" className="mp-cta-pill">Performance Marketing →</Link>
          <span className="mp-cta-more">+{platforms.length - 3} more services</span>
        </div>
      </div>

    </section>
  );
}
